import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { useTitle } from "utils/UseTitle";
import history from "utils/history";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import FeatureKeys from "global/constants/FeatureKeys";
import { useFeatureEnabled, useFeatures } from "hooks/use-feature";
import { hasAccessTo } from "layouts/Config";
import { openErrorNotification, openInfoNotification } from "helpers/methods";
import { fetchWebFormsByTemplate } from "screens/WebForms/WebFormsService";
import styles from "./AiWebForm.styles";
import tokens from "./AiWebForm.tokens";
import { useAiWebForm } from "./useAiWebForm";
import { aiBuilderPath, aiErrorMessage, replacesAnotherForm, templateLine } from "./AiWebFormHelper";
import { AiWarning, isValidMessage } from "./AiWebFormTypesAndValidation";
import { AiBuilderHeader, AiVersionButton } from "./components/AiBuilderHeader";
import { AiChatPanel } from "./components/AiChatPanel";
import { AiComposer } from "./components/AiComposer";
import { AiPreviewPanel } from "./components/AiPreviewPanel";
import { AiVersionsPanel } from "./components/AiVersionsPanel";
import { AiPublishModal } from "./components/AiPublishModal";
import { SparkleGlyph } from "./components/AiWebFormIcons";

const copy = strings.aiWebForm;

/** When the start's checklist moves to its next step: it is one call, so the steps follow a clock. */
const STARTING_STEP_MS = [6_000, 20_000];

interface PublishInfo {
  pageTexts: string[];
  labelWarnings: AiWarning[];
  replaces: boolean;
}

/**
 * "Build with AI": chat on one side, the live preview on the other. Below
 * 900 px the two become tabs (the phone frames), with the version picker in
 * the tab bar and the versions as a full-screen sheet; 900–1199 px keeps the
 * split with a narrower chat.
 */
const AiWebFormBuilder = () => {
  useTitle(title.WEBFORM_AI);
  const { sessionId: rawSessionId } = useParams<{ sessionId: string }>();
  const location = useLocation<{ prompt?: string } | undefined>();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const templateId = search.get("templateId") ?? "";
  const signerId = Number(search.get("signerId") ?? 0);
  const formName = search.get("name") ?? "";
  const prompt = location.state?.prompt;
  const sessionId = rawSessionId && rawSessionId !== "new" ? rawSessionId : null;
  const params = useMemo(
    () => ({ sessionId, templateId, signerId, name: formName, prompt }),
    [sessionId, templateId, signerId, formName, prompt],
  );

  const theme = useTheme();
  const phoneLayout = useMediaQuery(theme.breakpoints.down("md"));
  const wide = useMediaQuery(theme.breakpoints.up("lg"));
  const widest = useMediaQuery(theme.breakpoints.up("xl"));
  // The Try chips wrap (three rows, as designed) only where the conversation keeps its height.
  const tall = useMediaQuery("(min-height: 900px)");
  const bannerBesidePanel = useMediaQuery(`(min-width: ${tokens.layout.bannerBesidePanelMinWidth}px)`);
  const chatWidth = phoneLayout
    ? "100%"
    : widest
      ? tokens.layout.chatWidthXl
      : wide
        ? tokens.layout.chatWidthLg
        : tokens.layout.chatWidthMd;

  const aiEnabled = useFeatureEnabled(FeatureKeys.AI_ASSISTANT);
  const { loaded: featuresLoaded } = useFeatures();
  const canEdit = hasAccessTo(strings.resources.template, strings.updatePermission);

  const { state, send, preview, restore, publish, publishChecks, hide, loadEarlier, dismissStale, retry } =
    useAiWebForm(params);

  const [tab, setTab] = useState<"chat" | "preview">("chat");
  const [previewUpdated, setPreviewUpdated] = useState(false);
  const [device, setDevice] = useState<"desktop" | "phone">("desktop");
  const [showThankYou, setShowThankYou] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [text, setText] = useState("");
  const [startingStep, setStartingStep] = useState(0);
  const [publishTarget, setPublishTarget] = useState<number | null>(null);
  const [publishInfo, setPublishInfo] = useState<PublishInfo | null>(null);
  const [publishing, setPublishing] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  const starting = state.phase === "starting";
  const currentVersion = state.current?.version ?? null;

  // The start's checklist: read, build, then style.
  useEffect(() => {
    if (!starting) {
      setStartingStep(0);
      return;
    }
    const timers = STARTING_STEP_MS.map((ms, index) => setTimeout(() => setStartingStep(index + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, [starting]);

  // A new version while the phone shows the chat: a dot on the Preview tab.
  const lastVersionRef = useRef<number | null>(null);
  useEffect(() => {
    if (currentVersion != null && lastVersionRef.current != null && currentVersion !== lastVersionRef.current) {
      if (phoneLayout && tab === "chat") setPreviewUpdated(true);
    }
    lastVersionRef.current = currentVersion;
  }, [currentVersion, phoneLayout, tab]);

  const showTab = (next: "chat" | "preview") => {
    setTab(next);
    if (next === "preview") setPreviewUpdated(false);
  };

  const pageTitle = formName || state.title || state.template?.templateName || copy.buildWithAi;
  const line = templateLine(state.template);
  const ready = state.phase === "ready";
  const fieldsChanged = state.template?.fieldsMatch === false;
  const promptDisabled = state.turnRunning || !canEdit || fieldsChanged || !!state.previewing || !ready;

  const handlePromptSubmit = async (message: string) => {
    await send(message);
  };
  // Design get carries no turn: the versions list names it ("After message #3").
  const shownDesign = state.previewing ?? state.current;
  const shownListed = state.versions?.versions.find((v) => v.version === shownDesign?.version);
  const shown =
    shownDesign && shownListed
      ? { ...shownDesign, origin: shownListed.origin, turn: shownListed.turn, restoredFrom: shownListed.restoredFrom }
      : shownDesign;

  const unlayerPath = `${viewpaths.webFormsViewPath}/builder/new?templateId=${encodeURIComponent(
    templateId,
  )}&signerId=${signerId}&name=${encodeURIComponent(pageTitle)}`;

  const suggest = (value: string) => {
    setText(value);
    if (phoneLayout) showTab("chat");
    setTimeout(() => composerRef.current?.focus(), 0);
  };

  const sendMessage = async () => {
    const message = text.trim();
    if (!isValidMessage(message)) return;
    setText("");
    const taken = await send(message);
    if (!taken) setText(message);
  };

  const openVersion = (version: number) => {
    preview(version === currentVersion ? null : version);
    if (phoneLayout) showTab("preview");
  };

  /** Whether the signer has another active web form, which publishing this design replaces. */
  const replacesExisting = async () =>
    replacesAnotherForm(
      (await fetchWebFormsByTemplate(templateId).catch(() => [])) ?? [],
      signerId,
      state.designId,
    );

  const openPublish = async (version: number | null) => {
    if (version == null || !state.designId) return;
    setVersionsOpen(false);
    setPublishTarget(version);
    setPublishInfo(null);
    try {
      const [checks, replaces] = await Promise.all([publishChecks(version), replacesExisting()]);
      setPublishInfo({ ...checks, replaces });
    } catch (error: any) {
      openErrorNotification(aiErrorMessage(error) || copy.loadFailed);
      setPublishTarget(null);
    }
  };

  const confirmPublish = async () => {
    if (publishTarget == null || !publishInfo) return;
    setPublishing(true);
    const outcome = await publish(publishTarget, pageTitle, publishInfo.replaces);
    if (outcome?.published) {
      setPublishing(false);
      setPublishTarget(null);
      history.push(viewpaths.webFormsViewPath);
      return;
    }
    // A 409 for a publish that was not told to replace: another form may have been made for this
    // signer since the dialog opened. Checked again, the dialog shows the replace notice to confirm.
    if (outcome?.failure === "conflict" && !publishInfo.replaces && (await replacesExisting())) {
      setPublishInfo({ ...publishInfo, replaces: true });
      openInfoNotification(outcome.message || copy.replaceNoticeStrong);
    } else if (outcome) {
      openErrorNotification(outcome.message || copy.loadFailed);
    }
    setPublishing(false);
  };

  const buildNew = () =>
    history.push(aiBuilderPath({ sessionId: null, templateId, signerId, name: formName }), {});

  // ------------------------------------------------------------ whole-page states
  const unavailable = state.phase === "switchedOff" || (featuresLoaded && !aiEnabled);
  const failed = state.phase === "startFailed" || state.phase === "loadFailed" || state.phase === "notFound";
  if (unavailable || failed) {
    const message = unavailable
      ? copy.switchedOff
      : state.phase === "notFound"
        ? copy.notFound
        : state.error || copy.loadFailed;
    return (
      <Box sx={styles.page}>
        <AiBuilderHeader
          compact={phoneLayout}
          title={pageTitle}
          templateLine={line}
          publishedVersion={null}
          onBack={() => history.push(viewpaths.webFormsViewPath)}
          onPublish={() => undefined}
          publishDisabled
        />
        <Box sx={styles.stateBox}>
          <Box sx={styles.stateCard} role="alert">
            <Box sx={styles.designerTile} aria-hidden="true">
              <SparkleGlyph />
            </Box>
            <p>{message}</p>
            <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
              {!unavailable && state.phase !== "notFound" && (
                <Button variant="contained" onClick={retry}>
                  {copy.retry}
                </Button>
              )}
              {templateId && state.phase !== "loadFailed" && (
                <Button variant="outlined" onClick={() => history.push(unlayerPath)}>
                  {copy.withoutAi}
                </Button>
              )}
              {(state.phase === "notFound" || state.phase === "loadFailed") && (
                <Button variant="outlined" onClick={() => history.push(viewpaths.webFormsViewPath)}>
                  {copy.backToWebForms}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const versionButton = (
    <AiVersionButton
      version={starting ? null : currentVersion}
      isCurrent
      open={versionsOpen}
      disabled={!ready || !state.versions}
      onClick={() => setVersionsOpen((open) => !open)}
      showBadge={!phoneLayout}
    />
  );

  const versionsPanel = versionsOpen && ready && (
    <AiVersionsPanel
      versions={state.versions}
      selected={state.previewing?.version ?? currentVersion}
      sheet={phoneLayout}
      canEdit={canEdit && !fieldsChanged}
      onSelect={(version) => openVersion(version)}
      onRestore={(version) => restore(version)}
      onPublish={(version) => openPublish(version)}
      onClose={() => setVersionsOpen(false)}
    />
  );

  const composer = (
    <AiComposer
      ref={composerRef}
      value={text}
      onChange={setText}
      onSend={sendMessage}
      sendDisabled={!ready || state.turnRunning}
      readOnly={!canEdit}
    />
  );

  const showChat = !phoneLayout || tab === "chat";
  const showPreview = !phoneLayout || tab === "preview";

  return (
    <Box sx={styles.page}>
      <AiBuilderHeader
        compact={phoneLayout}
        title={pageTitle}
        templateLine={line}
        publishedVersion={state.publishedVersion}
        onBack={() => history.push(viewpaths.webFormsViewPath)}
        onPublish={() => openPublish(state.previewing?.version ?? currentVersion)}
        publishDisabled={
          !ready || !canEdit || !templateId || state.turnRunning || fieldsChanged || currentVersion == null
        }
        versionButton={phoneLayout ? undefined : versionButton}
      />
      {phoneLayout && (
        <Box sx={styles.tabBar}>
          <Box sx={styles.segmented} role="tablist" aria-label={copy.tabsLabel}>
            <Box
              component="button"
              type="button"
              role="tab"
              aria-selected={tab === "chat"}
              sx={styles.segment(tab === "chat")}
              onClick={() => showTab("chat")}
            >
              {copy.chatTab}
            </Box>
            <Box
              component="button"
              type="button"
              role="tab"
              aria-selected={tab === "preview"}
              sx={styles.segment(tab === "preview")}
              onClick={() => showTab("preview")}
            >
              {copy.previewTab}
              {previewUpdated && (
                <>
                  <Box component="span" sx={styles.updatedDot} aria-hidden="true" />
                  <Box component="span" sx={styles.srOnly}>
                    {` · ${copy.previewUpdated}`}
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Box sx={styles.tabSpacer} />
          {versionButton}
        </Box>
      )}
      <Box sx={styles.body}>
        {state.phase === "loading" ? (
          <Box sx={styles.stateBox} role="status" aria-label={copy.loading}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {showChat && (
              <AiChatPanel
                width={chatWidth}
                compact={!(widest && tall)}
                entries={state.entries}
                starting={starting}
                startingStep={startingStep}
                startingStyle={prompt?.trim() || undefined}
                turnRunning={state.turnRunning}
                stale={state.stale}
                onDismissStale={dismissStale}
                fieldsChanged={fieldsChanged}
                onBuildNew={buildNew}
                hasEarlier={state.nextBefore != null}
                loadingEarlier={state.loadingEarlier}
                onLoadEarlier={loadEarlier}
                onOpenVersion={openVersion}
                onSuggest={suggest}
                canEdit={canEdit && ready}
                onHide={ready ? hide : null}
                composer={composer}
              />
            )}
            {showPreview && (
              <AiPreviewPanel
                design={shown}
                previewing={!!state.previewing}
                compact={phoneLayout}
                device={device}
                onDevice={setDevice}
                showThankYou={showThankYou}
                onShowThankYou={setShowThankYou}
                onBackToCurrent={() => preview(null)}
                onRestore={(version) => restore(version)}
                canEdit={canEdit && !fieldsChanged}
                overlay={phoneLayout ? undefined : versionsPanel}
                hideBanner={versionsOpen && !bannerBesidePanel}
                onPromptSubmit={handlePromptSubmit}
                promptDisabled={promptDisabled}
              />
            )}
            {phoneLayout && versionsPanel}
          </>
        )}
      </Box>

      <AiPublishModal
        open={publishTarget != null}
        version={publishTarget}
        replaces={!!publishInfo?.replaces}
        pageTexts={publishInfo?.pageTexts ?? []}
        labelWarnings={publishInfo?.labelWarnings ?? []}
        fieldMap={state.current?.fieldMap}
        loading={!publishInfo}
        publishing={publishing}
        onClose={() => setPublishTarget(null)}
        onPublish={confirmPublish}
      />
    </Box>
  );
};

export default AiWebFormBuilder;
